export interface CyberAdvisorDashboardHistoryEvent {
  id: string;
  at: string;
  label: string;
  clientName?: string;
  detail?: string;
}

export type CyberAdvisorItemWorkflowStatus =
  | 'a_traiter'
  | 'en_attente_client'
  | 'a_valider'
  | 'briefing_genere'
  | 'donnee_corrigee'
  | 'pas_aujourdhui'
  | 'traite';

export interface CyberAdvisorCalendarBlock {
  id: string;
  startTime: string;
  endTime: string;
  label: string;
  clientName?: string;
  itemId?: string;
  action: string;
  durationMinutes: number;
}

export interface CyberAdvisorEmailDraft {
  subject: string;
  body: string;
}

export interface CyberAdvisorAutomationAction {
  id: string;
  label: string;
  itemId?: string;
  clientName?: string;
  status?: CyberAdvisorItemWorkflowStatus;
}

export interface CyberAdvisorDailyPlan {
  preparedAt: string;
  headline: string;
  focusItemIds: string[];
  correctionItemIds: string[];
  validationItemIds: string[];
  estimatedMinutes: number;
  calendarBlocks?: CyberAdvisorCalendarBlock[];
  emailDraft?: CyberAdvisorEmailDraft;
  priorityRationale?: string[];
  automationActions?: CyberAdvisorAutomationAction[];
  advisorKey?: string;
  advisorName?: string;
}


export interface CyberAdvisorScopedState {
  snoozedUntil: Record<string, string>;
  completedItems: Record<string, string>;
  notesByClient: Record<string, string>;
  historyEvents: CyberAdvisorDashboardHistoryEvent[];
  itemStatuses: Record<string, CyberAdvisorItemWorkflowStatus>;
  dailyPlan: CyberAdvisorDailyPlan | null;
}

export interface CyberAdvisorDashboardState {
  advisorName: string;
  snoozedUntil: Record<string, string>;
  completedItems: Record<string, string>;
  notesByClient: Record<string, string>;
  historyEvents: CyberAdvisorDashboardHistoryEvent[];
  itemStatuses: Record<string, CyberAdvisorItemWorkflowStatus>;
  dailyPlan: CyberAdvisorDailyPlan | null;
  stateByAdvisor: Record<string, CyberAdvisorScopedState>;
  updatedAt?: string | null;
}

export const DEFAULT_CYBER_ADVISOR_DASHBOARD_STATE: CyberAdvisorDashboardState = {
  advisorName: '',
  snoozedUntil: {},
  completedItems: {},
  notesByClient: {},
  historyEvents: [],
  itemStatuses: {},
  dailyPlan: null,
  stateByAdvisor: {},
  updatedAt: null,
};

const API_STATE = '/api/prisme/cyber/advisor-dashboard/state';
const API_HISTORY = '/api/prisme/cyber/advisor-dashboard/history';

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = (data as any)?.error || response.statusText || 'Erreur API bureau conseiller';
    throw new Error(message);
  }
  return data as T;
}

function normalizeState(raw: Partial<CyberAdvisorDashboardState> = {}): CyberAdvisorDashboardState {
  return {
    advisorName: typeof raw.advisorName === 'string' ? raw.advisorName : '',
    snoozedUntil: raw.snoozedUntil && typeof raw.snoozedUntil === 'object' ? raw.snoozedUntil : {},
    completedItems: raw.completedItems && typeof raw.completedItems === 'object' ? raw.completedItems : {},
    notesByClient: raw.notesByClient && typeof raw.notesByClient === 'object' ? raw.notesByClient : {},
    historyEvents: Array.isArray(raw.historyEvents) ? raw.historyEvents.slice(0, 50) : [],
    itemStatuses: raw.itemStatuses && typeof raw.itemStatuses === 'object' ? raw.itemStatuses : {},
    dailyPlan: raw.dailyPlan && typeof raw.dailyPlan === 'object' ? raw.dailyPlan : null,
    stateByAdvisor: raw.stateByAdvisor && typeof raw.stateByAdvisor === 'object' ? raw.stateByAdvisor : {},
    updatedAt: raw.updatedAt || null,
  };
}

export async function getCyberAdvisorDashboardState(): Promise<CyberAdvisorDashboardState> {
  const response = await fetch(API_STATE, { credentials: 'include' });
  const data = await readJson<{ state: CyberAdvisorDashboardState }>(response);
  return normalizeState(data.state);
}

export async function saveCyberAdvisorDashboardState(state: Partial<CyberAdvisorDashboardState>): Promise<CyberAdvisorDashboardState> {
  const response = await fetch(API_STATE, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ state }),
  });
  const data = await readJson<{ state: CyberAdvisorDashboardState }>(response);
  return normalizeState(data.state);
}

export async function appendCyberAdvisorDashboardHistory(event: CyberAdvisorDashboardHistoryEvent): Promise<CyberAdvisorDashboardState> {
  const response = await fetch(API_HISTORY, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event }),
  });
  const data = await readJson<{ state: CyberAdvisorDashboardState }>(response);
  return normalizeState(data.state);
}
